import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DndContext, DragOverlay, PointerSensor, closestCorners, useSensor, useSensors, useDroppable } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useSelector } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { postResource, deleteCommentEntities } from "../../../apis/apiService";
import { toast } from "react-toastify";
import "../task/SubtaskBoard.css";
import { Tooltip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button } from "@mui/material";
import { FiCalendar, FiUser, FiX, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { TbArrowUp, TbArrowRight, TbArrowDown } from "react-icons/tb";
import Loader from "../../common/Loader";

// ── helpers ─────────────────────────────────────────────────────────────────
const getInitials = (n = "") => n.split(" ").slice(0, 2).map(w => w[0]?.toUpperCase()).join("") || "?";
const PAL = ["#0052CC", "#00875A", "#FF5630", "#FF8B00", "#6554C0", "#00B8D9", "#36B37E", "#DE350B"];
const aColor = (n = "") => { let h = 0; for (const c of n) h = c.charCodeAt(0) + ((h << 5) - h); return PAL[Math.abs(h) % PAL.length]; };
const fmtDate = d => { if (!d) return null; const dt = new Date(d); return isNaN(dt) ? d : dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); };
const isOverdue = d => d && new Date(d) < new Date();
const SWATCHES = ["#0052CC", "#00875A", "#FF5630", "#FF8B00", "#6554C0", "#00B8D9", "#36B37E", "#DE350B", "#403294", "#172B4D"];

const PriorityIcon = ({ name = "", colour = "" }) => {
  const n = name.toUpperCase();
  const Icon = n === "HIGH" ? TbArrowUp : n === "LOW" ? TbArrowDown : TbArrowRight;
  const c = colour || (n === "HIGH" ? "#FF5630" : n === "LOW" ? "#36B37E" : "#FF8B00");
  return <Tooltip title={`Priority: ${name || "Medium"}`} placement="top" ><span style={{ color: c, display: "inline-flex", alignItems: "center" }}><Icon size={13} /></span></Tooltip>;
};

// ── Confirm Modal ───────────────────────────────────────────────────────────
const ConfirmModal = ({ isOpen, title, message, onConfirm, onCancel }) => {
  return (
    <Dialog open={isOpen} onClose={onCancel}>
      <DialogTitle>{title || "Confirm Deletion"}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message || "Are you sure?"}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>NO</Button>
        <Button onClick={onConfirm} color="error" sx={{ px: 3 }}>YES</Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Kanban Card ──────────────────────────────────────────────────────────────
const KanbanCard = ({ issue, colColor, onOpen, onDelete }) => {
  const [menu, setMenu] = useState(false);
  const menuRef = useRef(null);
  const due = issue.due_date;
  const late = isOverdue(due);
  const assigneeName = issue.assignee || issue.assignee_name || "";
  const reporterName = issue.reporter || issue.reporter_name || "";

  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({ id: issue.id });

  useEffect(() => {
    if (!menu) return;
    const handler = e => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menu]);

  const login_user = useSelector((state) => state.selectOptions?.loginUserName);
  // Flow: Admin/Manager have full access. Clients can't drag. Employees can drag if assignee or reporter.
  const isAssignee = (
    Number(issue?.assignee_id) === Number(login_user?.id) ||
    Number(issue?.assigned_to) === Number(login_user?.id) ||
    (issue?.assignee && typeof issue.assignee === "string" && issue.assignee.toLowerCase() !== "unassigned" && issue.assignee.toLowerCase() === login_user?.name?.toLowerCase()) ||
    (issue?.assignee_name && typeof issue.assignee_name === "string" && issue.assignee_name.toLowerCase() !== "unassigned" && issue.assignee_name.toLowerCase() === login_user?.name?.toLowerCase())
  );

  const isReporter = (
    Number(issue?.added_by) === Number(login_user?.id) ||
    Number(issue?.reporter_id) === Number(login_user?.id) ||
    Number(issue?.created_by) === Number(login_user?.id) ||
    (issue?.reporter && typeof issue.reporter === "string" && issue.reporter.toLowerCase() !== "unassigned" && issue.reporter.toLowerCase() === login_user?.name?.toLowerCase()) ||
    (issue?.reporter_name && typeof issue.reporter_name === "string" && issue.reporter_name.toLowerCase() !== "unassigned" && issue.reporter_name.toLowerCase() === login_user?.name?.toLowerCase())
  );

  const canDrag = (
    ["ADMIN", "MANAGER"].includes(login_user?.role) ||
    (login_user?.role !== "CLIENT" && (isAssignee || isReporter))
  );

  const cardInner = (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.3 : 1,
      }}
      className={`jk-card ${!canDrag ? "jk-card--locked" : ""}`}
      {...(canDrag ? attributes : {})}
      {...(canDrag ? listeners : {})}
    >
      <div className="jk-card-stripe" style={{ background: colColor }} />
      <div className="jk-card-inner">
        <div className="jk-card-head">
          <Tooltip title="View & Edit Issue" placement="top">
            <span
              className="jk-card-title"
              onClick={(e) => {
                e.stopPropagation();
                onOpen(issue);
              }}
            >
              {issue.title || "Untitled Issue"}
            </span>
          </Tooltip>
          <div className="jk-card-dates-box">
            <Tooltip
              title={`Due Date: ${fmtDate(issue.due_date) || "No due date"}`}
              placement="top"
            >
              <div className={`jk-card-date-icon-wrap${late ? " late" : ""}`}>
                <FiCalendar size={14} />
              </div>
            </Tooltip>
          </div>
        </div>

        <div className="jk-card-mid">
          <span className="jk-card-key">#{issue.id}</span>
          <div className="jk-av-stack">
            {assigneeName && assigneeName.toLowerCase() !== "unassigned" ? (
              <Tooltip title={`Assignee: ${assigneeName}`} placement="top">
                <div className="jk-av" style={{ background: aColor(assigneeName) }}>
                  {getInitials(assigneeName)}
                </div>
              </Tooltip>
            ) : (
              <div
                style={{
                  fontSize: "10px",
                  color: "#666",
                  backgroundColor: "#f8f9fa",
                  padding: "2px 8px",
                  borderRadius: "12px",
                  border: "1px dashed #ccc",
                  display: "inline-flex",
                  alignItems: "center",
                  fontWeight: "500",
                }}
              >
                Unassigned
              </div>
            )}
            {reporterName && reporterName.toLowerCase() !== "unassigned" && (
              <Tooltip title={`Reporter: ${reporterName}`} placement="top">
                <div className="jk-av" style={{ background: aColor(reporterName) }}>
                  {getInitials(reporterName)}
                </div>
              </Tooltip>
            )}
          </div>
        </div>

        <div className="jk-card-bottom">
          {issue.priority_name && (
            <span
              className="jk-card-prio-text"
              style={{ color: issue.priority_colour || "#888" }}
            >
              {issue.priority_name}
            </span>
          )}

          {issue.severity_name && (
            <span
              className="jk-card-status-text"
              style={{
                color: issue.severity_colour || "#888",
                marginLeft: "10px",
                fontSize: "10px",
                fontWeight: "bold",
              }}
            >
              {issue.severity_name}
            </span>
          )}

          <div className="jk-card-fr">
            {issue.priority_name && (
              <PriorityIcon
                name={issue.priority_name}
                colour={issue.priority_colour}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return !canDrag ? (
    <Tooltip
      title="You don't have permission to move this issue"
      placement="top"
      arrow
      slotProps={{
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, -65],
              },
            },
          ],
        },
      }}
    >
      {cardInner}
    </Tooltip>
  ) : (
    cardInner
  );
};

const GhostCard = ({ issue }) => (
  <div className="jk-card jk-card-ghost">
    <div className="jk-card-stripe" style={{ background: "#0052CC" }} />
    <div className="jk-card-inner"><span className="jk-card-title">{issue?.title || "Issue"}</span></div>
  </div>
);

// ── Column ───────────────────────────────────────────────────────────────────
const Column = ({ col, issues, onOpen, onDelete, collapsedMap, onToggle, isClient }) => {
  const collapsed = collapsedMap[col.id];
  const ids = issues.map(i => i.id);

  const { setNodeRef } = useDroppable({ id: col.id });

  return (
    <div className={`jk-col${collapsed ? " jk-col--collapsed" : ""}`}>
      {/* Header */}
      <div className="jk-col-header">
        {!collapsed && <>
          <span className="jk-col-name">{col.name.toUpperCase()}</span>
          <span className="jk-col-count">{issues.length}</span>
        </>}
        <div className="jk-col-actions">
          <button className="jk-col-btn" onClick={() => onToggle(col.id)}>
            {collapsed ? <FiChevronDown size={13} /> : <FiChevronUp size={13} />}
          </button>
        </div>
      </div>

      <div className="jk-col-bar" style={{ background: col.color }} />

      {!collapsed && (
        <div ref={setNodeRef} className="jk-cards-zone">
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            {issues.map(i => <KanbanCard key={i.id} issue={i} colColor={col.color} onOpen={onOpen} onDelete={onDelete} />)}
          </SortableContext>
          {issues.length === 0 && !isClient && (
            <div className="jk-drop-zone" style={{ cursor: "default" }}>Drop issues here</div>
          )}
        </div>
      )}

      {collapsed && (
        <div className="jk-col-vertical">
          <div className="jk-col-vdot" style={{ background: col.color }} />
          <span className="jk-col-vname">{col.name}</span>
          <span className="jk-col-vcount">{issues.length}</span>
        </div>
      )}
    </div>
  );
};

const IssueBoard = ({ ProjectID, allIssues, onRefresh, loading, onOpenIssue, searchTerm = "" }) => {
  const { id: routeId } = useParams();
  const project_id = localStorage.getItem("project_id");
  const navigate = useNavigate();
  const location = useLocation();

  const StatusOptions = useSelector(s => s.selectOptions?.StatusOptions);
  const login_user = useSelector((state) => state.selectOptions?.loginUserName);
  const isClient = login_user?.role === "CLIENT";

  const statusList = useMemo(() => {
    const grp = StatusOptions?.find(s => s.status_type === "ISSUE");
    const allDetails = grp?.status_details ?? [];
    const kanbanFiltered = allDetails.filter(
      s => s.is_kanban_view === 1 || s.is_kanban_view === "1" || s.is_kanban_view === true
    );
    const activeList = kanbanFiltered.length > 0 ? kanbanFiltered : allDetails;

    return activeList.map(s => ({
      id: s.status_id,
      name: s.status_name,
      color: s.status_colour || "#CCCCCC"
    }));
  }, [StatusOptions]);

  const [columns, setColumns] = useState([]);
  const [issueMap, setIssueMap] = useState({});
  const [activeIssue, setActiveIssue] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [confirm, setConfirm] = useState({ open: false, issue: null });
  const [search, setSearch] = useState("");

  useEffect(() => {
    setSearch(searchTerm || "");
  }, [searchTerm]);

  const [updating, setUpdating] = useState(false);
  const startColRef = useRef(null);
  const lastOverColRef = useRef(null);

  // ── Build columns ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (updating) return;
    if (!statusList.length) return;
    if (loading && (!allIssues || allIssues.length === 0)) return;

    const map = {};
    allIssues.forEach(i => { map[i.id] = i; });
    setIssueMap(map);

    const cols = statusList.map(s => ({
      ...s,
      taskIds: (allIssues || []).filter(i => Number(i.current_status_id) === Number(s.id)).map(i => i.id),
    }));

    const claimed = new Set(cols.flatMap(c => c.taskIds));
    const leftover = (allIssues || []).filter(i => !claimed.has(i.id)).map(i => i.id);
    if (cols.length && leftover.length) cols[0] = { ...cols[0], taskIds: [...cols[0].taskIds, ...leftover] };

    setColumns(cols);
  }, [allIssues, statusList, loading]);

  // ── Filter ────────────────────────────────────────────────────────────────
  const visibleIssues = useCallback((colId) => {
    const col = columns.find(c => c.id === colId);
    if (!col) return [];
    return col.taskIds.map(id => issueMap[id]).filter(i => {
      if (!i) return false;
      const searchLower = search ? search.trim().toLowerCase() : "";
      const statusName = i.current_status_name || i.status_name || col.name || "";
      const titleOk = !search ||
        (i.title || "").toLowerCase().includes(searchLower) ||
        (i.reporter || "").toLowerCase().includes(searchLower) ||
        (i.assignee || "").toLowerCase().includes(searchLower) ||
        String(i.id).includes(searchLower) ||
        statusName.toLowerCase().includes(searchLower);
      return titleOk;
    });
  }, [columns, issueMap, search]);

  // ── Drag & Drop ───────────────────────────────────────────────────────────
  const sensors = useSensors(useSensor(PointerSensor, { 
    activationConstraint: isClient ? { distance: 9999 } : { distance: 6 } 
  }));

  const colOf = id => {
    if (!id) return null;
    const col = columns.find(c => c.taskIds.some(tid => String(tid) === String(id)));
    return col ? col.id : null;
  };

  const onDragStart = ({ active }) => {
    setActiveIssue(issueMap[active.id] ?? null);
    startColRef.current = colOf(active.id);
    lastOverColRef.current = colOf(active.id);
  };

  const onDragOver = ({ active, over }) => {
    if (!over) return;
    const activeId = active.id;
    const overId = over.id;

    const from = colOf(activeId);
    const to = columns.find(c => String(c.id) === String(overId)) ? overId : colOf(overId);

    if (!from || !to || String(from) === String(to)) return;

    if (String(lastOverColRef.current) === String(to)) return;
    lastOverColRef.current = to;

    setColumns(prev => prev.map(c => {
      if (String(c.id) === String(from)) return { ...c, taskIds: c.taskIds.filter(id => String(id) !== String(activeId)) };
      if (String(c.id) === String(to)) {
        const idx = c.taskIds.findIndex(id => String(id) === String(overId));
        const ids = [...c.taskIds];
        idx === -1 ? ids.push(activeId) : ids.splice(idx, 0, activeId);
        return { ...c, taskIds: ids };
      }
      return c;
    }));
  };

  const onDragEnd = async ({ active, over }) => {
    const activeId = active.id;
    const overId = over?.id;
    const from = startColRef.current;
    setActiveIssue(null);
    startColRef.current = null;
    lastOverColRef.current = null;

    if (!overId) return;

    const to = columns.find(c => String(c.id) === String(overId)) ? overId : colOf(overId);

    if (!from || !to) return;

    if (String(from) === String(to)) {
      setColumns(prev => prev.map(c => {
        if (String(c.id) !== String(from)) return c;
        const oldIdx = c.taskIds.findIndex(id => String(id) === String(activeId));
        const newIdx = c.taskIds.findIndex(id => String(id) === String(overId));
        if (oldIdx === -1 || newIdx === -1) return c;
        return { ...c, taskIds: arrayMove(c.taskIds, oldIdx, newIdx) };
      }));
      return;
    }

    // Optimistic update
    setIssueMap(prev => ({ ...prev, [activeId]: { ...prev[activeId], current_status_id: to } }));

    setUpdating(true);
    try {
      const numericIssueId = activeId.toString().replace(/\D/g, "");
      await postResource("issue", "update", numericIssueId, {
        current_status_id: to,
        issue_id: numericIssueId
      });
      toast.success("Issue status updated");
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e?.message || "Update failed");
      if (onRefresh) onRefresh();
    } finally {
      setUpdating(false);
    }
  };

  const openIssue = i => {
    if (onOpenIssue) {
      onOpenIssue(i);
    } else {
      navigate(`/projects/projectDetail/${project_id}/issue/IssueDetail/${i.id}`, { state: { viewMode: "board", from: location.pathname } });
    }
  };

  const handleDeleteIssue = (issue) => {
    setConfirm({ open: true, issue });
  };

  const executeDelete = async () => {
    const issueId = confirm.issue?.id?.toString().replace(/\D/g, "");
    if (!issueId) return;
    try {
      await deleteCommentEntities("issue/delete", issueId);
      toast.success("Issue deleted successfully");
      setConfirm({ open: false, issue: null });
      onRefresh();
    } catch (e) {
      toast.error(e?.message || "Failed to delete issue");
    }
  };

  return (
    <>
      <div className="jk-root" style={{ position: "relative" }}>
        {loading && !updating && <Loader />}

        {updating && (
          <>
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 50,
              backgroundColor: "rgba(255, 255, 255, 0.4)", backdropFilter: "blur(3px)", borderRadius: "8px"
            }} />
            <div style={{
              position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 51,
              display: "flex", flexDirection: "column", alignItems: "center"
            }}>
              <div className="jk-spinner" />
              <span style={{ marginTop: "10px", fontWeight: "600", color: "#555" }}>
                Updating status...
              </span>
            </div>
          </>
        )}

        {(!loading || updating) && (
          <div className="jk-bwrap" style={{ position: "relative", minHeight: "300px" }}>
            <DndContext sensors={sensors} collisionDetection={closestCorners}
              onDragStart={onDragStart} onDragOver={onDragOver} onDragEnd={onDragEnd}>
              <div className="jk-board">
                {columns.map(col => (
                  <Column key={col.id} col={col} issues={visibleIssues(col.id)}
                    onOpen={openIssue} onDelete={handleDeleteIssue} onRefresh={onRefresh}
                    collapsedMap={collapsed} onToggle={id => setCollapsed(p => ({ ...p, [id]: !p[id] }))}
                    isClient={isClient} />
                ))}
              </div>
              <DragOverlay dropAnimation={{ duration: 200 }}>
                {activeIssue ? <GhostCard issue={activeIssue} /> : null}
              </DragOverlay>
            </DndContext>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirm.open}
        title="Confirm Deletion"
        message={`Are you sure you want to delete the issue "${confirm.issue?.title}"?`}
        onConfirm={executeDelete}
        onCancel={() => setConfirm({ open: false, issue: null })}
      />
    </>
  );
};


export default IssueBoard;
